// Base (undiscounted) prices per document type, for places that quote a
// price before the generator is open (the post-purchase upsell paywall).
// The generators themselves stay the source of truth for what is charged:
// pay stubs use bundle pricing and the Accounting Mockup depends on the
// template, so these are the single-document starting prices.
export const DOC_BASE_PRICES = {
  paystub: 9.99,
  "canadian-paystub": 9.99,
  "uk-payslip": 9.99,
  "ai-resume": 9.99,
  "offer-letter": 9.99,
  w2: 14.99,
  w9: 14.99,
  "1099-nec": 14.99,
  "1099-misc": 14.99,
  "schedule-c": 14.99,
  "commercial-lease": 9.99,
  "utility-bill": 9.99,
  "cease-and-desist": 9.99,
  "power-of-attorney": 9.99,
  "vehicle-bill-of-sale": 9.99,
  "bank-statement": 49.99,
};

// The admin's prices (Preferences > Document prices), fetched once a minute
// and shared by every generator; an override wins over the built-in, and a
// zero price makes the document free for everyone, guests included.
import { useEffect, useState } from "react";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const TTL = 60 * 1000;
let priceCache = null;
let priceAt = 0;
let priceInflight = null;
const listeners = new Set();

export function loadDocPrices(force = false) {
  if (!force && priceCache && Date.now() - priceAt < TTL) return Promise.resolve(priceCache);
  if (!priceInflight) {
    priceInflight = fetch(`${BACKEND_URL}/api/document-prices?_=${Date.now()}`)
      .then((r) => r.json())
      .then((d) => { priceCache = d && d.success && d.prices && typeof d.prices === "object" ? d.prices : (priceCache || {}); })
      .catch(() => { priceCache = priceCache || {}; })
      .then(() => { priceAt = Date.now(); priceInflight = null; listeners.forEach((fn) => fn(priceCache)); return priceCache; });
  }
  return priceInflight;
}
export const docPriceOverrides = () => priceCache || {};
export const effectiveDocPrice = (type, fallback) => { const o = docPriceOverrides()[type]; return typeof o === "number" ? o : fallback; };
export const isFreePrice = (p) => Number(p) === 0;

// The override map, live: a page re-renders when the prices arrive
export function useDocPrices() {
  const [prices, setPrices] = useState(() => priceCache || {});
  useEffect(() => {
    let on = true;
    const fn = (p) => { if (on) setPrices(p || {}); };
    listeners.add(fn);
    loadDocPrices().then(fn);
    return () => { on = false; listeners.delete(fn); };
  }, []);
  return prices;
}
// One document's price: the admin's, else the number the generator has always used
export function useDocPrice(type, fallback) {
  const prices = useDocPrices();
  const o = prices[type];
  return typeof o === "number" ? o : fallback;
}

export const docBasePrice = (type) => effectiveDocPrice(type, DOC_BASE_PRICES[type] ?? null);

// "$9.99", or "free" at zero, for a sentence
export const priceLabel = (p) => (Number(p) === 0 ? "free" : `$${Number(p || 0).toFixed(2)}`);
// The cheapest of several documents, as the admin prices them
export function useLowestDocPrice(types) {
  const prices = useDocPrices();
  return Math.min(...types.map((t) => (typeof prices[t] === "number" ? prices[t] : (DOC_BASE_PRICES[t] ?? Infinity))));
}
// What documents start at, across everything we sell
export function useStartingPrice() {
  return useLowestDocPrice(Object.keys(DOC_BASE_PRICES));
}
